import { get } from 'lodash';
import {
  type RestExperience,
  type RestModalCard,
  type RestModalForwards,
} from '../contracts';
import {
  EventSource,
  RestModalAction,
  type EventObject,
  type EventSyncObject,
  type SubmissionConfig,
} from '../events';
import { type FunnelSurvey } from './survey';

type ExperienceEventContext = Pick<
  RestExperience,
  'session' | 'id' | 'name' | 'version' | 'user'
>;

type SurveyEventContext = {
  experience: ExperienceEventContext;
  survey: FunnelSurvey;
};

export type FunnelEventQueue = {
  events?: EventObject[];
  syncs?: EventSyncObject[];
  synchronous?: boolean;
};

export type FunnelBackwardsAction = {
  name: 'submit_section_deflect_button';
  direction: 'backwards';
  type?: string;
  action: string;
  url?: string;
  validation?: string;
  form: RestModalForwards['sync'];
  component: { id: string; name: string };
};

export const createSurveyEvent = ({
  experience: { session, id, version, user },
  survey,
  surveyType,
  cardVersion,
  inputType,
  key,
  value,
}: SurveyEventContext & {
  surveyType: string;
  cardVersion?: string;
  inputType: string;
  key: keyof FunnelSurvey;
  value: string | boolean;
}): EventObject => {
  const event: EventObject = {
    session,
    id,
    name: surveyType,
    event: 'radio_selected',
    version,
    app_id: (user && user.app_id) || null,
    card_version: cardVersion,
    properties: {
      type: inputType,
      name: key,
      option: value,
      survey_state: survey,
    },
  };
  switch (key) {
    case 'competition':
      event.event = 'select_selected';
      break;
    case 'sentiment':
      event.event = 'nps_selected';
      break;
    case 'feedback':
      event.event = 'textarea_typed';
      break;
    case 'reason_other':
      event.event = 'reason_other';
      break;
    case 'competition_other':
      event.event = 'competition_other';
      break;
    case 'confirmation':
      event.event = value ? 'checkbox_checked' : 'checkbox_unchecked';
      break;
    default:
      break;
  }
  return event;
};

export const createPageLoadedEvent = ({
  experience: { session, id, name, version, user },
  survey,
}: SurveyEventContext): EventObject => ({
  session,
  id,
  name,
  event: 'page_loaded',
  version,
  app_id: (user && user.app_id) || null,
  user_id: get(user, 'user.id', null),
  properties: { survey_state: survey },
});

const modalProperties = (modal: RestModalCard, survey: FunnelSurvey) => ({
  survey_state: survey,
  modal,
  sync: { ...get(modal, 'requires.forwards.sync', {}) },
  backwardsSync: { ...get(modal, 'requires.backwards.sync', {}) },
});

export const createModalOpenedEvent = ({
  experience: { session, version, user },
  survey,
  modal,
}: SurveyEventContext & { modal: RestModalCard }): EventObject => ({
  session,
  id: get(modal, 'id', 'no_id'),
  name: get(modal, 'name', undefined),
  event: 'modal_opened',
  version,
  card_version: get(modal, 'version'),
  app_id: get(user, 'app_id', null),
  user_id: get(user, 'user.id', null),
  properties: modalProperties(modal, survey),
});

export const createModalClosedEvents = ({
  experience: { session, version, user },
  survey,
  modal,
  previousModal,
  includeRejectedOffer,
}: SurveyEventContext & {
  modal: RestModalCard;
  previousModal: RestModalCard;
  includeRejectedOffer: boolean;
}): EventObject[] => {
  // Identity comes from the closed modal; metadata comes from current state.
  const closed: EventObject = {
    session,
    id: previousModal.id,
    name: previousModal.name,
    version,
    card_version: get(previousModal, 'version'),
    app_id: get(user, 'app_id', null),
    user_id: get(user, 'user.id', null),
    event: 'modal_closed',
    properties: modalProperties(modal, survey),
  };
  return includeRejectedOffer
    ? [closed, { ...closed, event: 'rejected_offer' }]
    : [closed];
};

export const createNevermindQueue = ({
  experience: { session, version, user },
  survey,
  action,
}: SurveyEventContext & {
  action: FunnelBackwardsAction;
}): FunnelEventQueue => ({
  syncs: [
    {
      session,
      version,
      app_id: (user && user.app_id) || null,
      survey_state: survey,
      ...action,
    },
  ],
  events: [
    {
      session,
      version,
      app_id: (user && user.app_id) || null,
      id: action.component.id,
      ...action,
      event: 'nevermind',
      properties: { survey_state: survey },
    },
  ],
  synchronous: true,
});

export const createDirectCancelQueue = ({
  experience: { session, id, name, version, user },
  survey,
  cardVersion,
  action: { url, type, sync, validation },
}: SurveyEventContext & {
  cardVersion?: string;
  action: RestModalForwards;
}): FunnelEventQueue => ({
  events: [
    {
      session,
      id,
      name: `direct_click_cancel_${name}`,
      version,
      app_id: (user && user.app_id) || null,
      form: sync,
      event: 'cancel',
      card_version: cardVersion,
      properties: { survey_state: survey, sync },
    },
  ],
  syncs: [
    {
      session,
      name: `direct_click_cancel_${name}`,
      version,
      app_id: (user && user.app_id) || null,
      form: sync,
      direction: 'forwards',
      action: RestModalAction.link,
      id,
      validation,
      url,
      type,
      survey_state: survey,
    },
  ],
  synchronous: true,
});

type PreparedFunnelEventQueue = FunnelEventQueue & {
  syncs?: (EventSyncObject & { source?: EventSource })[];
  config: SubmissionConfig;
};

export const prepareFunnelEventQueue = (
  queue: FunnelEventQueue,
  {
    isPricingPage,
    isLifecycle,
    config,
  }: {
    isPricingPage?: boolean;
    isLifecycle?: boolean;
    config: SubmissionConfig;
  }
): PreparedFunnelEventQueue => ({
  ...queue,
  events: queue.events?.map((event) => ({
    ...event,
    source: isPricingPage
      ? EventSource.PRICING_PAGE
      : isLifecycle
        ? EventSource.LIFECYCLE_OFFER
        : undefined,
  })),
  syncs: queue.syncs?.map((sync) => ({
    ...sync,
    source: isLifecycle ? EventSource.LIFECYCLE_OFFER : undefined,
  })),
  config,
});
